import React from 'react';

import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';


import { AnchorLink } from '@src/components/AnchorLink';
import {
    Trainings,
    ProductUpdates,
} from '@src/components/common/Content/ContentSpotlights';
import FindGuidelines from '@src/components/common/Navigation/FindGuidelines';
import { SafelyRenderHtml } from '@src/services';
import { getProductPageVisibilityLabels } from '@src/utils/getStatesFromProducts';

import BundleSection from './BundleSection';
import DiscountsSection from './DiscountsSection';
import EndTextSection from './EndTextSection';
import FeaturesSection from './FeaturesSection';
import OverviewSection from './OverviewSection';
import ProductSection from './ProductSection';
import RelatedProduct from './RelatedProduct';
import RenderAvailability from './RenderAvailability';

import './page.scss';

const STRUCTURED_SECTIONS = {
    'new-overview': OverviewSection,
    'overview': OverviewSection,
    'features-and-benefits': FeaturesSection,
    'discounts': DiscountsSection,
    'bundle': BundleSection,
    'end-text': EndTextSection,
};

const ProductPage = ({
    allStates,
    appHistory,
    comingSoonStates = [],
    currentProduct,
    expiredAt,
    hasAccess = true,
    isAdmin: isAdminProp,
    isNew,
    pageStatus,
    publishedAt,
    productStaticData,
    productUpdates,
    relatedProduct,
    status,
    states,
    trainings,
}) => {
    const accessLevel = useSelector((state) => state?.getIn?.(['app', 'userInfo', 'accessLevel']));
    const isAdmin = isAdminProp ?? (accessLevel === 'admin');

    const {
        hasRelated,
        legacySlug,
        agent360Slug,
        name,
    } = relatedProduct;

    const isSectionBlank = (section) => {
        if (section.slug === 'guides-and-rate-bulletins') {
            return !hasAccess;
        }

        if (section.slug === 'related-articles') {
            return !productUpdates || productUpdates.length === 0;
        }

        if (section.slug === 'training-and-job-aids') {
            return !trainings.trainings || trainings.trainings.length === 0;
        }

        return false;
    };

    const renderedNav = () => {
        return (
            <div className="col sticky-top d-none d-lg-block text-center justify-content-center">
                <nav className="nav justify-content-center main-container-row">
                    {
                        productStaticData.sections.map((section) => {
                            if (section.status !== 'published') {
                                return null;
                            }

                            if (section.showInMenu === 'false') {
                                return null;
                            }

                            if (isSectionBlank(section)) {
                                return null;
                            }

                            return (
                                <AnchorLink
                                    key={`fixed-header-${section.slug}`}
                                    smooth
                                    className="nav-link p-md-2 p-xl-2"
                                    title={SafelyRenderHtml(section.title)}
                                    to={`#${section.slug}`}
                                />
                            );
                        })
                    }
                </nav>
            </div>
        );
    };

    const renderSection = (section) => {
        if (section.slug === 'guides-and-rate-bulletins') {
            return (
                <FindGuidelines product={currentProduct} />
            );
        }

        if (section.slug === 'related-articles') {
            return (
                <ProductUpdates
                    product={currentProduct}
                    productUpdates={productUpdates}
                />
            );
        }

        if (section.slug === 'training-and-job-aids') {
            return (
                <Trainings

                    onProductPage
                    trainings={trainings.trainings}
                />
            );
        }
    };

    const renderedSections = () => {
        return productStaticData.sections.map((section) => {
            if (section.status !== 'published') {
                return null;
            }

            if (isSectionBlank(section)) {
                return null;
            }

            const StructuredComponent = STRUCTURED_SECTIONS[section.slug];
            if (StructuredComponent && section.data) {
                return (
                    <StructuredComponent
                        key={`section-${section.slug}`}
                        data={section.data}
                        id={section.slug}
                    />
                );
            }

            if (section.html) {
                return (
                    <ProductSection
                        key={`section-${section.slug}`}
                        id={section.slug}
                        content={section.html}
                    />
                );
            }

            return (
                <section
                    key={`section-${section.slug}`}
                    id={section.slug}
                    className="col-12 p-0"
                >
                    {renderSection(section)}
                </section>
            );
        });
    };

    const renderTitle = () => {
        if (!productStaticData.title) {
            return null;
        }

        let titleContent = productStaticData.title;
        const visibilityLabels = getProductPageVisibilityLabels({
            expiredAt,
            isAdmin,
            pageStatus,
            publishedAt,
            status,
        }).map((label) => (
            `<span class="${label.className} text-uppercase mr-1">[${label.text}]</span>`
        ));

        if (visibilityLabels.length > 0) {
            const labels = `${visibilityLabels.join(' ')} `;
            if (/<h1[^>]*>/i.test(titleContent)) {
                titleContent = titleContent.replace(/(<h1[^>]*>)/i, `$1${labels}`);
            } else {
                titleContent = `${labels}${titleContent}`;
            }
        }

        return SafelyRenderHtml(titleContent, false);
    };

    return (
        <Row className="product-page-container row-full-width">
            {
                hasRelated && agent360Slug
                && (
                    <RelatedProduct>
                        <>
                            Looking for the Agent360<sup>®</sup> supported {name} insurance product?
                            { ' ' }
                            <Link to={`/products/${agent360Slug}`} className="link-primary">
                                Take Me There <span className="visually-hidden"> Link</span>
                            </Link>
                        </>
                    </RelatedProduct>
                )
            }
            {
                hasRelated && legacySlug
                && (
                    <RelatedProduct>
                        <>
                            Looking for the ARS supported {name} insurance product?
                            { ' ' }
                            <Link to={`/products/${legacySlug}`} className="link-primary">
                                Take Me There <span className="visually-hidden"> Link</span>
                            </Link>
                        </>
                    </RelatedProduct>
                )
            }
            {
                productStaticData.title
                && (
                    <Col xs={12} className="text-center">
                        <React.Fragment key={`product-static-data-${productStaticData.title}`}>
                            {renderTitle()}
                        </React.Fragment>
                        <RenderAvailability
                            allStates={allStates}
                            comingSoonStates={comingSoonStates}
                            isNew={isNew}
                            slug={currentProduct}
                            states={states}
                        />
                    </Col>
                )
            }
            {renderedNav()}
            {renderedSections()}
        </Row>
    );
};

ProductPage.propTypes = {
    allStates: PropTypes.array,
    appHistory: PropTypes.object,
    comingSoonStates: PropTypes.array,
    currentProduct: PropTypes.string.isRequired,
    expiredAt: PropTypes.string,
    hasAccess: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isNew: PropTypes.bool,
    pageStatus: PropTypes.string,
    publishedAt: PropTypes.string,
    productStaticData: PropTypes.shape({
        title: PropTypes.string,
        sections: PropTypes.arrayOf(PropTypes.shape({
            data: PropTypes.object,
            html: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
            showInMenu: PropTypes.string,
            slug: PropTypes.string,
            status: PropTypes.string,
            title: PropTypes.string,
        })).isRequired,
    }).isRequired,
    productUpdates: PropTypes.arrayOf(PropTypes.object),
    relatedProduct: PropTypes.shape({
        hasRelated: PropTypes.bool,
        legacySlug: PropTypes.string,
        agent360Slug: PropTypes.string,
        name: PropTypes.string,
    }),
    status: PropTypes.string,
    states: PropTypes.arrayOf(PropTypes.shape({
        abbreviation: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    })),
    trainings: PropTypes.shape({
        trainings: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

export default ProductPage;
